import React, { useReducer, useEffect, useRef } from 'react';
import {
    Box, Grid, TextField, Button, Typography, CircularProgress, Snackbar, Alert as MuiAlert,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select,
    MenuItem, Chip, Card, CardContent, Pagination, Stack, IconButton, Tooltip,
} from '@mui/material';
import {
    Receipt, Visibility, Edit,
    CheckCircle, HourglassEmpty, Warning, Assessment, FileDownload, Email, OpenInNew
} from '@mui/icons-material';
import TableCollapsible from '../../components/TableCollapsible';
import api from '../../services/api';
import * as qs from 'qs';
import { format, parseISO } from 'date-fns';
import XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';

Chart.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

// Estado inicial
const initialState = {
    charges: [],
    loading: false,
    exportingExcel: false,
    filters: {
        affiliate: '',
        document: '',
        status: '',
        type: '',
        yearMonth: '',
        state: ''
    },
    pagination: {
        page: 1,
        pageSize: 50,
        total: 0,
        pageCount: 0
    },
    stateAlert: {
        status: false,
        type: 'success',
        msg: ''
    },
    dialog: {
        open: false,
        type: '', // 'view', 'edit', 'generate'
        data: null
    },
    editForm: {
        status: '',
        payment_date: '',
        saving: false
    },
    dashboardData: {
        totalReceitas: 0,
        totalPendente: 0,
        totalVencido: 0,
        totalPago: 0,
        chargesByStatus: [],
        chargesByMonth: [],
        chargesByState: []
    },
    affiliates: [],
    loadingAffiliates: false
};

// Reducer
function financeReducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };

        case 'SET_EXPORTING_EXCEL':
            return { ...state, exportingExcel: action.payload };

        case 'SET_CHARGES':
            return { ...state, charges: action.payload };

        case 'SET_PAGINATION':
            return { ...state, pagination: { ...state.pagination, ...action.payload } };

        case 'SET_FILTERS':
            return { ...state, filters: { ...state.filters, ...action.payload } };

        case 'RESET_FILTERS':
            return { ...state, filters: initialState.filters };

        case 'SET_ALERT':
            return { ...state, stateAlert: action.payload };

        case 'SET_DIALOG':
            return { ...state, dialog: { ...state.dialog, ...action.payload } };

        case 'CLOSE_DIALOG':
            return { ...state, dialog: { open: false, type: '', data: null } };

        case 'SET_DASHBOARD_DATA':
            return { ...state, dashboardData: { ...state.dashboardData, ...action.payload } };

        case 'SET_AFFILIATES':
            return { ...state, affiliates: action.payload };

        case 'SET_LOADING_AFFILIATES':
            return { ...state, loadingAffiliates: action.payload };

        case 'SET_EDIT_FORM':
            return { ...state, editForm: { ...state.editForm, ...action.payload } };

        default:
            return state;
    }
}

export default function FinanceiroAfiliados() {
    const [state, dispatch] = useReducer(financeReducer, initialState);
    const searchTimer = useRef(null);
    const navigate = useNavigate();

    function maskDateBR(value) {
        const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }

    function isoToDateMask(isoDate) {
        if (!isoDate) return '';
        const datePart = String(isoDate).slice(0, 10);
        const [year, month, day] = datePart.split('-');
        if (!year || !month || !day) return '';
        return `${day}/${month}/${year}`;
    }

    function dateMaskToIso(dateMask) {
        const dateValue = String(dateMask || '').trim();
        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateValue);
        if (!match) return null;

        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        const date = new Date(year, month - 1, day);

        const isValidDate =
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day;

        if (!isValidDate) return null;

        return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    const Alert = React.forwardRef(function Alert(props, ref) {
        return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    });

    // Status das cobranças
    const STATUS_OPTIONS = [
        { value: 'AGUARDANDO PAGAMENTO', label: 'Aguardando Pagamento', color: 'warning' },
        { value: 'PAGO', label: 'Pago', color: 'success' }, 
        { value: 'ATRASADA', label: 'Atrasada', color: 'error' },
    ];

    // Tipos de cobrança
    const TYPE_OPTIONS = [
        { value: 'RPPS_ASSOCIADO', label: 'RPPS Associado' },
        { value: 'RPPS_OUTROS_ESTADOS', label: 'RPPS de Outros Estados' },
        { value: 'PATROCINADOR', label: 'Patrocinador' },
        { value: 'ISENTO', label: 'Isento' },
        { value: 'STAFF', label: 'Staff' }
    ];

    // Estados brasileiros
    const STATES = [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
        'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
        'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ];

    // Colunas da tabela
    const columns = [
        { Header: "N° Cobrança", accessor: "charge_number" },
        { Header: "Afiliado", accessor: "affiliate_name" },
        { Header: "Estado", accessor: "state" },
        { Header: "Tipo", accessor: "affiliate_type" },
        { Header: "Competência", accessor: "competence" },
        { Header: "Vencimento", accessor: "due_date" },
        { Header: "Valor", accessor: "amount" },
        { Header: "Status", accessor: "status" },
        { Header: "Email", accessor: "affiliate_email" },
    ];

    // Buscar afiliados para filtro
    async function loadAffiliates(searchText = '') {
        dispatch({ type: 'SET_LOADING_AFFILIATES', payload: true });
        try {
            const params = {
                filters: {
                    active: { $ne: false },
                    ...(searchText && {
                        company: {
                            social_reason: { $containsi: searchText }
                        }
                    })
                },
                sort: ['createdAt:desc'],
                populate: {
                    company: {
                        fields: ['social_reason', 'state']
                    }
                },
                pagination: {
                    pageSize: 50
                },
                encodeValuesOnly: true
            };

            const queryString = qs.stringify(params);
            const response = await api.get(`/affiliates?${queryString}`);
            dispatch({ type: 'SET_AFFILIATES', payload: response.data.data });
        } catch (error) {
            console.error('Erro ao carregar afiliados:', error);
        } finally {
            dispatch({ type: 'SET_LOADING_AFFILIATES', payload: false });
        }
    }

    // Buscar cobranças (usando tabela affiliate-payment)
    async function loadCharges() {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const { affiliate, document, status, type, yearMonth, state: filterState } = state.filters;
            const { page, pageSize } = state.pagination;

            const params = {
                filters: {
                    ...(affiliate && {
                        affiliate: {
                            company: {
                                social_reason: { $containsi: affiliate }
                            }
                        }
                    }),
                    ...(document && {
                        affiliate: {
                            company: {
                                document: { $containsi: document }
                            }
                        }
                    }),
                    ...(status && { status: { $eq: status } }),
                    ...(type && {
                        affiliate: {
                            type: { $eq: type }
                        }
                    }),
                    ...(yearMonth && {
                        reference: { $containsi: yearMonth }
                    }),
                    ...(filterState && {
                        affiliate: {
                            company: {
                                state: { $eq: filterState }
                            }
                        }
                    })
                },
                sort: ['payment_date:desc', 'createdAt:desc'],
                populate: {
                    affiliate: {
                        fields: ['type', 'active', 'instituicoesRelacionadas'],
                        populate: {
                            company: {
                                fields: ['social_reason', 'state', 'city', 'document', 'email']
                            }
                        }
                    }
                },
                pagination: {
                    page,
                    pageSize
                },
                encodeValuesOnly: true
            };

            const queryString = qs.stringify(params);
            const response = await api.get(`/affiliate-payments?${queryString}`);

            const formattedCharges = response.data.data.map(charge => ({
                ...charge,
                charge_number: charge.attributes.external_id || charge.id,
                affiliate_name: charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.social_reason || 'N/A',
                state: charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.state || 'N/A',
                affiliate_email: charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.email || '',
                affiliate_type: charge.attributes.affiliate?.data?.attributes?.type || 'N/A',
                competence: String(charge.attributes.external_id).slice(0, 4) || 'N/A',
                due_date: charge.attributes?.bolecode?.data?.dado_boleto?.dados_individuais_boleto[0]?.data_vencimento
                    ? format(parseISO(charge.attributes.bolecode.data.dado_boleto.dados_individuais_boleto[0].data_vencimento), 'dd/MM/yyyy')
                    : 'N/A',
                amount: charge.attributes.total
                    ? charge.attributes.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                    : 'R$ 0,00',
                status: charge.attributes.status || 'PENDENTE'
            }));

            dispatch({ type: 'SET_CHARGES', payload: formattedCharges });
            dispatch({
                type: 'SET_PAGINATION',
                payload: {
                    total: response.data.meta.pagination.total,
                    pageCount: response.data.meta.pagination.pageCount
                }
            });
        } catch (error) {
            console.error('Erro ao carregar cobranças:', error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'Erro ao carregar cobranças'
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }

    // Buscar dados do dashboard
    async function loadDashboardData() {
        try {
            const { affiliate, document, status, type, yearMonth, state: filterState } = state.filters;

            const params = {
                filters: {
                    ...(affiliate && {
                        affiliate: {
                            company: {
                                social_reason: { $containsi: affiliate }
                            }
                        }
                    }),
                    ...(document && {
                        affiliate: {
                            company: {
                                document: { $containsi: document }
                            }
                        }
                    }),
                    ...(status && { status: { $eq: status } }),
                    ...(yearMonth && {
                        reference: { $containsi: yearMonth }
                    }),
                    ...(type && {
                        affiliate: {
                            type: { $eq: type }
                        }
                    }),
                    ...(filterState && {
                        affiliate: {
                            company: {
                                state: { $eq: filterState }
                            }
                        }
                    })
                },
                populate: {
                    affiliate: {
                        fields: ['type', 'active'],
                        populate: {
                            company: {
                                fields: ['state','city', 'social_reason']
                            }
                        }
                    }
                },
                pagination: {
                    pageSize: 200 // Buscar todos para estatísticas
                },
                encodeValuesOnly: true
            };

            const queryString = qs.stringify(params);
            const response = await api.get(`/affiliate-payments?${queryString}`);
            const allCharges = response.data.data;

            // Calcular estatísticas
            const totalPago = allCharges
                .filter(c => c.attributes.status === 'PAGO')
                .reduce((sum, c) => sum + (c.attributes.total || 0), 0);

            const totalPendente = allCharges
                .filter(c => ['PENDENTE', 'AGUARDANDO PAGAMENTO'].includes(c.attributes.status))
                .reduce((sum, c) => sum + (c.attributes.total || 0), 0);

            const totalVencido = allCharges
                .filter(c => c.attributes.status === 'ATRASADA')
                .reduce((sum, c) => sum + (c.attributes.total || 0), 0);

            const totalReceitas = totalPago + totalPendente + totalVencido;

            // Agrupar por status
            const chargesByStatus = STATUS_OPTIONS.map(status => ({
                status: status.label,
                count: allCharges.filter(c => c.attributes.status === status.value).length,
                amount: allCharges
                    .filter(c => c.attributes.status === status.value)
                    .reduce((sum, c) => sum + (c.attributes.total || 0), 0)
            }));

            // Agrupar por ano de referência
            const chargesByMonth = allCharges.reduce((acc, charge) => {
                const competence = charge.attributes.reference || 'N/A';
                if (!acc[competence]) {
                    acc[competence] = { competence, count: 0, amount: 0 };
                }
                acc[competence].count++;
                acc[competence].amount += charge.attributes.total || 0;
                return acc;
            }, {});

            // Agrupar por estado
            const chargesByState = allCharges.reduce((acc, charge) => {
                const state = charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.state || 'N/A';
                if (!acc[state]) {
                    acc[state] = { state, count: 0, amount: 0 };
                }
                acc[state].count++;
                acc[state].amount += charge.attributes.total || 0;
                return acc;
            }, {});

            dispatch({
                type: 'SET_DASHBOARD_DATA',
                payload: {
                    totalReceitas,
                    totalPendente,
                    totalVencido,
                    totalPago,
                    chargesByStatus,
                    chargesByMonth: Object.values(chargesByMonth).sort((a, b) =>
                        b.competence.localeCompare(a.competence)
                    ).slice(0, 6),
                    chargesByState: Object.values(chargesByState).sort((a, b) =>
                        b.amount - a.amount
                    ).slice(0, 10)
                }
            });
        } catch (error) {
            console.error('Erro ao carregar dados do dashboard:', error);
        }
    }

    // Exportar para Excel
    async function handleExportExcel() {
        if (state.charges.length === 0) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Nenhuma cobrança para exportar'
                }
            });
            return;
        }

        dispatch({ type: 'SET_EXPORTING_EXCEL', payload: true });
        try {
            // Buscar todos os registros para exportação
            const { affiliate, document, status, type, yearMonth, state: filterState } = state.filters;

            let page = 1;
            const pageSize = 100;
            let allCharges = [];

            while (true) {
                const params = {
                    filters: {
                        ...(affiliate && {
                            affiliate: {
                                company: {
                                    social_reason: { $containsi: affiliate }
                                }
                            }
                        }),
                        ...(document && {
                            affiliate: {
                                company: {
                                    document: { $containsi: document }
                                }
                            }
                        }),
                        ...(status && { status: { $eq: status } }),
                        ...(type && {
                            affiliate: {
                                type: { $eq: type }
                            }
                        }),
                        ...(yearMonth && {
                            reference: { $containsi: yearMonth }
                        }),
                        ...(filterState && {
                            affiliate: {
                                company: {
                                    state: { $eq: filterState }
                                }
                            }
                        })
                    },
                    sort: ['payment_date:desc'],
                    populate: {
                        affiliate: {
                            fields: ['type', 'active'],
                            populate: {
                                company: {
                                    fields: ['social_reason', 'state', 'city', 'document', 'email']
                                }
                            }
                        }
                    },
                    pagination: {
                        page,
                        pageSize
                    },
                    encodeValuesOnly: true
                };

                const queryString = qs.stringify(params);
                const response = await api.get(`/affiliate-payments?${queryString}`);
                const chargesData = response.data.data;

                if (!chargesData || chargesData.length === 0) break;

                allCharges.push(...chargesData);
                page++;
            }

            // Formatar dados para Excel
            const sheetData = allCharges.map(charge => ({
                'N° Cobrança': charge.attributes.external_id || charge.id,
                'Afiliado': charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.social_reason || 'N/A',
                'Email': charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.email || 'N/A',
                'CNPJ': charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.document || 'N/A',
                'Estado': charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.state || 'N/A',
                'Cidade': charge.attributes.affiliate?.data?.attributes?.company?.data?.attributes?.city || 'N/A',
                'Tipo':  charge.attributes.affiliate?.data?.attributes?.type || 'N/A',
                'Referência': String(charge.attributes.external_id).slice(0, 4) || 'N/A',
                'Valor': charge.attributes.total || 0,
                'Status': charge.attributes.status || 'PENDENTE',
                'Nosso Número': charge.attributes.external_id || '',
                'URL Boleto': charge.attributes.payment_url || '',
                'Data Pagamento': charge.attributes.payment_date
                    ? format(parseISO(charge.attributes.payment_date), 'dd/MM/yyyy')
                    : 'N/A',
                'Criado em': format(parseISO(charge.attributes.createdAt), 'dd/MM/yyyy HH:mm')
            }));

            const sheet = XLSX.utils.json_to_sheet(sheetData);
            const workBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workBook, sheet, 'Cobranças Afiliados');

            const fileName = `cobrancas-afiliados-${format(new Date(), 'yyyyMMdd-HHmmss')}.xlsx`;
            await XLSX.writeFile(workBook, fileName);

            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'success',
                    msg: 'Planilha exportada com sucesso!'
                }
            });
        } catch (error) {
            console.error('Erro ao exportar planilha:', error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'Erro ao exportar planilha'
                }
            });
        } finally {
            dispatch({ type: 'SET_EXPORTING_EXCEL', payload: false });
        }
    }

    // Visualizar cobrança
    function handleView(charge) {
        dispatch({
            type: 'SET_DIALOG',
            payload: {
                open: true,
                type: 'view',
                data: charge
            }
        });
    }

    function getBoletoUrl(charge) {
        return (
            charge?.attributes?.payment_url ||
            charge?.attributes?.bolecode?.data?.dado_boleto?.link_boleto ||
            charge?.attributes?.bolecode?.data?.dado_boleto?.url ||
            charge?.attributes?.bolecode?.data?.dado_boleto?.dados_individuais_boleto?.[0]?.link
        );
    }

    function handleViewBoleto(charge) {
        const boletoUrl = getBoletoUrl(charge);

        if (!boletoUrl) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Esta cobrança ainda não possui URL de boleto disponível.'
                }
            });
            return;
        }

        window.open(boletoUrl, '_blank', 'noopener,noreferrer');
    }

    function handleShortcutEditAffiliate(charge) {
        const affiliateId = charge?.attributes?.affiliate?.data?.id;

        if (!affiliateId) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Não foi possível identificar o afiliado desta cobrança.'
                }
            });
            return;
        }

        dispatch({ type: 'CLOSE_DIALOG' });
        navigate(`/Filiados?editAffiliateId=${affiliateId}`);
    }

    // Editar cobrança
    function handleEdit(charge) {
        dispatch({
            type: 'SET_DIALOG',
            payload: {
                open: true,
                type: 'edit',
                data: charge
            }
        });
        dispatch({
            type: 'SET_EDIT_FORM',
            payload: {
                status: charge.attributes?.status || 'PENDENTE',
                payment_date: charge.attributes?.payment_date
                    ? isoToDateMask(charge.attributes.payment_date)
                    : charge.attributes?.updatedAt
                        ? isoToDateMask(charge.attributes.updatedAt)
                    : ''
            }
        });
    }

    // Salvar edição
    async function handleSaveEdit() {
        if (!state.dialog.data) return;

        const paymentDateIso = state.editForm.payment_date
            ? dateMaskToIso(state.editForm.payment_date)
            : null;
        const paymentDateISODateTime = paymentDateIso ? `${paymentDateIso}T12:00:00.000Z` : null;

        if (state.editForm.status === 'PAGO' && !state.editForm.payment_date) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Data de pagamento é obrigatória quando o status for PAGO.'
                }
            });
            return;
        }

        if (state.editForm.payment_date && !paymentDateIso) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Informe uma data válida no formato DD/MM/AAAA.'
                }
            });
            return;
        }

        try {
            dispatch({ type: 'SET_EDIT_FORM', payload: { saving: true } });

            const response = await api.put(`/affiliate-payments/${state.dialog.data.id}`, {
                data: {
                    status: state.editForm.status,
                    payment_date: paymentDateISODateTime
                }
            });

            if (response.status === 200) {
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'success',
                        msg: 'Status atualizado com sucesso!'
                    }
                });
                dispatch({ type: 'CLOSE_DIALOG' });
                await loadCharges();
                await loadDashboardData();
            }
        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: error.response?.data?.error?.message || 'Erro ao atualizar status'
                }
            });
        } finally {
            dispatch({ type: 'SET_EDIT_FORM', payload: { saving: false } });
        }
    }

    // Gerar boleto
    async function handleGenerateBoleto(charge) {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            console.log(charge)
            // Chamada para o endpoint de geração de boleto
            const response = await api.put(`/affiliate-payments/${charge.id}`, {
                data: {...charge.attributes }

            });
            if (response.data.success) {
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'error',
                        msg: response.data.message 
                    }
                });
                // Se houver URL do boleto, abre em nova aba
                if (response.data.data?.payment_url) {
                    window.open(response.data.data.payment_url, '_blank');
                }

                await loadCharges();
                await loadDashboardData();
            }
        } catch (error) {
            console.error('Erro ao gerar boleto:', error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: error.response?.data?.error?.message || 'Erro ao gerar boleto'
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }

    // Cancelar cobrança
    async function handleCancelCharge(charge) {
        if (!window.confirm('Tem certeza que deseja cancelar esta cobrança?')) return;

        try {
            dispatch({ type: 'SET_LOADING', payload: true });

            await api.put(`/affiliate-payments/${charge.id}/cancel`, {
                motivo: 'Cancelado pelo usuário'
            });

            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'success',
                    msg: 'Cobrança cancelada com sucesso!'
                }
            });
            await loadCharges();
            await loadDashboardData();
        } catch (error) {
            console.error('Erro ao cancelar cobrança:', error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'Erro ao cancelar cobrança'
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }

    // Dados para o gráfico de pizza (Status)
    const pieData = {
        labels: state.dashboardData.chargesByStatus.map(s => s.status),
        datasets: [{
            data: state.dashboardData.chargesByStatus.map(s => s.count),
            backgroundColor: state.dashboardData.chargesByStatus.map(s => {
                // Verde para PAGO
                if (s.status === 'Pago') return '#43a047';
                // Vermelho para ATRASADA
                if (s.status === 'Atrasada') return '#e53935';
                // Cinza para todos os outros (PENDENTE, AGUARDANDO PAGAMENTO, etc)
                return '#757575';
            }),
            borderWidth: 2
        }]
    };

    // Dados para o gráfico de barras (Por mês)
    const barDataMonth = {
        labels: state.dashboardData.chargesByMonth.map(m => m.competence),
        datasets: [{
            label: 'Valor (R$)',
            data: state.dashboardData.chargesByMonth.map(m => m.amount),
            backgroundColor: '#1976d2',
            borderRadius: 4
        }]
    };

    // Dados para o gráfico de barras (Por estado)
    const barDataState = {
        labels: state.dashboardData.chargesByState.map(s => s.state),
        datasets: [{
            label: 'Valor (R$)',
            data: state.dashboardData.chargesByState.map(s => s.amount),
            backgroundColor: '#43a047',
            borderRadius: 4
        }]
    };

    // Buscar ao montar o componente
    useEffect(() => {
        loadCharges();
        loadDashboardData();
        loadAffiliates();
    }, []);

    // Buscar quando alterar página
    useEffect(() => {
        if (state.pagination.page > 1) {
            loadCharges();
        }
    }, [state.pagination.page]);

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }} p={3}>
            <Snackbar
                open={state.stateAlert.status}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => dispatch({ type: 'SET_ALERT', payload: { ...state.stateAlert, status: false } })}
            >
                <Alert severity={state.stateAlert.type}>
                    {state.stateAlert.msg}
                </Alert>
            </Snackbar>

            <Stack direction="column" spacing={3}>
                {/* Título */}
                <Typography variant="h4" fontWeight="bold">
                    Painel Financeiro - Afiliados
                </Typography>

                {/* Cards de resumo */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ backgroundColor: '#f3f3f3' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Total de Receitas
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold">
                                            {state.dashboardData.totalReceitas.toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL'
                                            })}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: '50%',
                                            backgroundColor: '#1976d2',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Assessment sx={{ color: 'white' }} />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ backgroundColor: '#f3f3f3' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Recebido
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" color="success.main">
                                            {state.dashboardData.totalPago.toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL'
                                            })}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: '50%',
                                            backgroundColor: '#43a047',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <CheckCircle sx={{ color: 'white' }} />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ backgroundColor: '#f3f3f3' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Pendente
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" color="warning.main">
                                            {state.dashboardData.totalPendente.toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL'
                                            })}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: '50%',
                                            backgroundColor: '#fb8c00',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <HourglassEmpty sx={{ color: 'white' }} />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ backgroundColor: '#f3f3f3' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Vencido
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold" color="error.main">
                                            {state.dashboardData.totalVencido.toLocaleString('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL'
                                            })}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            borderRadius: '50%',
                                            backgroundColor: '#e53935',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Warning sx={{ color: 'white' }} />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Gráficos */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ backgroundColor: '#f3f3f3', height: 340 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Typography variant="h6" textAlign="center" mb={2}>
                                    Cobranças por Status
                                </Typography>
                                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Box sx={{ width: 280, height: 240 }}>
                                        <Pie
                                            data={pieData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { position: 'bottom' }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ backgroundColor: '#f3f3f3', height: 340 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Typography variant="h6" textAlign="center" mb={2}>
                                    Receitas por Mês
                                </Typography>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Bar
                                        data={barDataMonth}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (ctx) => `R$ ${ctx.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                    }
                                                }
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        callback: (value) => `R$ ${value.toLocaleString('pt-BR')}`
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ backgroundColor: '#f3f3f3', height: 340 }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Typography variant="h6" textAlign="center" mb={2}>
                                    Top 10 Estados
                                </Typography>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Bar
                                        data={barDataState}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            indexAxis: 'y',
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (ctx) => `R$ ${ctx.parsed.x.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        callback: (value) => `R$ ${value.toLocaleString('pt-BR')}`
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filtros */}
                <Card sx={{ backgroundColor: '#f9f9f9' }}>
                    <CardContent>
                        <Typography variant="h6" mb={2}>Filtros</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    label="Buscar Afiliado"
                                    variant="outlined"
                                    value={state.filters.affiliate}
                                    onChange={(e) => {
                                        dispatch({
                                            type: 'SET_FILTERS',
                                            payload: { affiliate: e.target.value }
                                        });
                                        clearTimeout(searchTimer.current);
                                        searchTimer.current = setTimeout(() => {
                                            loadAffiliates(e.target.value);
                                        }, 500);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    label="CNPJ"
                                    variant="outlined"
                                    value={state.filters.document}
                                    onChange={(e) =>
                                        dispatch({
                                            type: 'SET_FILTERS',
                                            payload: { document: e.target.value }
                                        })
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={state.filters.status}
                                        label="Status"
                                        onChange={(e) =>
                                            dispatch({
                                                type: 'SET_FILTERS',
                                                payload: { status: e.target.value }
                                            })
                                        }
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {STATUS_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Tipo</InputLabel>
                                    <Select
                                        value={state.filters.type}
                                        label="Tipo"
                                        onChange={(e) =>
                                            dispatch({
                                                type: 'SET_FILTERS',
                                                payload: { type: e.target.value }
                                            })
                                        }
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {TYPE_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Estado</InputLabel>
                                    <Select
                                        value={state.filters.state}
                                        label="Estado"
                                        onChange={(e) =>
                                            dispatch({
                                                type: 'SET_FILTERS',
                                                payload: { state: e.target.value }
                                            })
                                        }
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {STATES.map(state => (
                                            <MenuItem key={state} value={state}>
                                                {state}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={1}>
                                <TextField
                                    fullWidth
                                    label="Competência (AAAA)"
                                    variant="outlined"
                                    placeholder="2026"
                                    value={state.filters.yearMonth}
                                    onChange={(e) =>
                                        dispatch({
                                            type: 'SET_FILTERS',
                                            payload: { yearMonth: e.target.value }
                                        })
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={1}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    sx={{ height: '56px' }}
                                    onClick={() => {
                                        dispatch({
                                            type: 'SET_PAGINATION',
                                            payload: { page: 1 }
                                        });
                                        loadCharges();
                                        loadDashboardData();
                                    }}
                                >
                                    Buscar
                                </Button>
                            </Grid>
                        </Grid>

                        <Box mt={2} display="flex" gap={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    dispatch({ type: 'RESET_FILTERS' });
                                    dispatch({
                                        type: 'SET_PAGINATION',
                                        payload: { page: 1 }
                                    });
                                }}
                            >
                                Limpar Filtros
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<FileDownload />}
                                onClick={handleExportExcel}
                                disabled={state.exportingExcel}
                            >
                                {state.exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Tabela */}
                {state.loading ? (
                    <Box display="flex" justifyContent="center" py={5}>
                        <CircularProgress size={50} />
                    </Box>
                ) : (
                    <>
                        <TableCollapsible
                            title="Cobranças de Afiliados"
                            variant="secondary"
                            rows={state.charges}
                            columns={columns}
                            customActions={(row) => (
                                <Box display="flex" gap={1}>
                                    <Tooltip title="Visualizar">
                                        <IconButton
                                            size="small"
                                            color="info"
                                            onClick={() => handleView(row)}
                                        >
                                            <Visibility />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleEdit(row)}
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Visualizar Boleto">
                                        <IconButton
                                            size="small"
                                            color="secondary"
                                            onClick={() => handleViewBoleto(row)}
                                        >
                                            <OpenInNew />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Gerar Boleto">
                                        <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() => handleGenerateBoleto(row)}
                                            disabled={row.attributes?.status === 'AGUARDANDO PAGAMENTO' || row.attributes?.status === 'PAGO' || row.attributes?.status === 'CANCELADO'}
                                        >
                                            <Receipt />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={row.affiliate_email || 'Email não disponível'}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="default"
                                                component="a"
                                                href={row.affiliate_email ? `mailto:${row.affiliate_email}` : undefined}
                                                disabled={!row.affiliate_email}
                                            >
                                                <Email />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                        />

                        <Stack direction="row" justifyContent="flex-end" mt={2}>
                            <Pagination
                                count={state.pagination.pageCount}
                                page={state.pagination.page}
                                color="primary"
                                onChange={(_, value) =>
                                    dispatch({
                                        type: 'SET_PAGINATION',
                                        payload: { page: value }
                                    })
                                }
                            />
                        </Stack>
                    </>
                )}
            </Stack>

            {/* Dialog de Visualização/Edição */}
            <Dialog
                open={state.dialog.open}
                onClose={() => dispatch({ type: 'CLOSE_DIALOG' })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {state.dialog.type === 'view' ? 'Detalhes da Cobrança' : 'Editar Cobrança'}
                </DialogTitle>
                <DialogContent>
                    {state.dialog.data && (
                        <Grid container spacing={2} mt={1}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>N° Cobrança:</strong> {state.dialog.data.charge_number}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Afiliado:</strong> {state.dialog.data.affiliate_name}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>CNPJ:</strong> {state.dialog.data.attributes?.affiliate?.data?.attributes?.company?.data?.attributes?.document || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Estado:</strong> {state.dialog.data.state}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Tipo:</strong> {state.dialog.data.affiliate_type}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Competência:</strong> {state.dialog.data.competence}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Vencimento:</strong> {state.dialog.data.due_date}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Valor:</strong> {state.dialog.data.amount}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                {state.dialog.type === 'view' ? (
                                    <Chip
                                        label={state.dialog.data.status}
                                        color={
                                            state.dialog.data.status === 'PAGO' ? 'success' :
                                                state.dialog.data.status === 'PENDENTE' ? 'warning' :
                                                    state.dialog.data.status === 'ATRASADA' ? 'error' : 'default'
                                        }
                                    />
                                ) : (
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={state.editForm.status}
                                            label="Status"
                                            onChange={(e) =>
                                                dispatch({
                                                    type: 'SET_EDIT_FORM',
                                                    payload: { status: e.target.value }
                                                })
                                            }
                                        >
                                            {STATUS_OPTIONS.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={6}>
                                {state.dialog.type === 'view' ? (
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Data de Pagamento:</strong>{' '}
                                        {state.dialog.data.attributes?.payment_date
                                            ? format(parseISO(state.dialog.data.attributes.payment_date), 'dd/MM/yyyy')
                                            : 'N/A'}
                                    </Typography>
                                ) : (
                                    <TextField
                                        fullWidth
                                        type="text"
                                        label="Data de Pagamento"
                                        placeholder="DD/MM/AAAA"
                                        required={state.editForm.status === 'PAGO'}
                                        value={state.editForm.payment_date}
                                        onChange={(e) =>
                                            dispatch({
                                                type: 'SET_EDIT_FORM',
                                                payload: { payment_date: maskDateBR(e.target.value) }
                                            })
                                        }
                                        inputProps={{ maxLength: 10 }}
                                        helperText={state.editForm.status === 'PAGO' ? 'Obrigatória para status PAGO' : 'Formato: DD/MM/AAAA'}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            </Grid>
                            {state.dialog.type === 'edit' && (
                                <Grid item xs={12}>
                                    <Button
                                        variant="outlined"
                                        color="info"
                                        onClick={() => handleShortcutEditAffiliate(state.dialog.data)}
                                    >
                                        Editar Filiado
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => dispatch({ type: 'CLOSE_DIALOG' })} disabled={state.editForm.saving}>
                        {state.dialog.type === 'view' ? 'Fechar' : 'Cancelar'}
                    </Button>
                    {state.dialog.type === 'edit' && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveEdit}
                            disabled={state.editForm.saving}
                        >
                            {state.editForm.saving ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
